<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { useCompetenceObservationRelations } from '@/composables/useCompetenceObservationRelations';
import CompetenceDomainIcon from '@/core/competences/CompetenceDomainIcon.vue';
import PerformanceLevelRatingInput from '@/core/performance-visuals/PerformanceLevelRatingInput.vue';
import { computed, toRefs } from 'vue';

const emit = defineEmits<{
  'update:observation': [value: Components.Schemas.CompetenceObservationDto];
}>();

const props = defineProps<{
  assessment: Components.Schemas.CompetenceAssessmentDto | Components.Schemas.BasePerformanceAssessmentDto;
  availableCompetenceDomains: Components.Schemas.CompetenceDomainDto[];
  availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
  disabled?: boolean;
  observation: Components.Schemas.CompetenceObservationDto;
}>();
const { availableCompetenceDomains, availableSchoolSubjects } = toRefs(props);

const { getCompetenceForObservation, getCompetenceDomainForObservation, getSchoolSubjectColorForObservation } =
  useCompetenceObservationRelations({
    availableCompetenceDomains,
    availableSchoolSubjects,
  });

const competenceDescription = computed<string>(() => getCompetenceForObservation(props.observation)?.description ?? '');
const competenceDomainSymbol = computed<string>(
  () => getCompetenceDomainForObservation(props.observation)?.symbol ?? '',
);
const schoolSubjectColor = computed<string | undefined>(() => getSchoolSubjectColorForObservation(props.observation));

const handlePerformanceLevelChange = (value: number | null): void => {
  emit('update:observation', {
    ...props.observation,
    performanceLevelV2Id: value,
  });
};
</script>

<template>
  <li class="flex items-center gap-md px-md py-sm">
    <div class="flex min-w-0 flex-1 items-center gap-md">
      <CompetenceDomainIcon :color="schoolSubjectColor" :icon-name="competenceDomainSymbol" />
      <span class="min-w-0 flex-1 truncate text-body font-normal" :title="competenceDescription">
        {{ competenceDescription }}
      </span>
    </div>
    <PerformanceLevelRatingInput
      class="shrink-0"
      :accessible-label="competenceDescription"
      :disabled="disabled"
      :model-value="observation.performanceLevelV2Id"
      :performance-visual="assessment.performanceVisual"
      @update:model-value="handlePerformanceLevelChange"
    />
  </li>
</template>
